/**
 * Spec 054, step 3: the words of an order's chat with its client and of the client's passport,
 * kept apart from the screens so a test reads them. Times are the device's clock.
 */
import type { ChatSummaryOut, ClientChatOut, ClientMessageOut, ClientPassportOut, ClientPassportRevisionOut } from "@/api/orders";
import { formatFileSize } from "@/lib/format";
import { clock } from "@/lib/lead";

/** While «Переписка» is open it asks the server again this often. */
export const CHAT_EVERY = 15_000;

export const MESSAGE_STATE: Record<ClientMessageOut["status"], string> = {
  received: "",
  queued: "в очереди",
  sent: "отправлено",
  failed: "не доставлено",
};

/** The line under a message: who wrote it, when, and what became of it. */
export function messageMeta(m: ClientMessageOut, now: Date = new Date()): string {
  const parts = [m.direction === "in" ? "Клиент" : m.author, clock(m.created_at, now)];
  if (MESSAGE_STATE[m.status]) parts.push(MESSAGE_STATE[m.status]);
  if (m.status === "failed" && m.error) parts.push(m.error);
  return parts.join(" · ");
}

/** «ТЗ.pdf · 1,2 МБ». */
export function fileLine(file: { name: string; size: number }): string {
  return `${file.name} · ${formatFileSize(file.size)}`;
}

/** The pages loaded so far, oldest first, each message once. */
export function mergeMessages(...pages: ClientMessageOut[][]): ClientMessageOut[] {
  const byId = new Map<string, ClientMessageOut>();
  for (const page of pages) for (const m of page) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
}

/** The chat in one line: bound or not, who leads it, how many went without the client's answer. */
export function chatLine(chat: Pick<ClientChatOut, "bound" | "manager" | "outgoing_in_a_row" | "monologue_limit">): string {
  const parts = [chat.bound ? "Чат с клиентом подключён" : "Чат с клиентом не подключён"];
  if (chat.manager) parts.push(`ведёт ${chat.manager.name}`);
  if (chat.outgoing_in_a_row > 0) parts.push(`без ответа клиента: ${chat.outgoing_in_a_row} из ${chat.monologue_limit}`);
  return parts.join(" · ");
}

/** Who saved a text: the staff member whose run did, a person's own agent, or the person. */
export function authorLine(person: string | null, agent: string | null, viaAgent: boolean): string {
  if (agent) return `${agent} (ИИ-агент)`;
  if (!person) return "";
  return viaAgent ? `${person} через своего агента` : person;
}

/** The passport's last save: its version, who, when and from what. */
export function passportLine(p: ClientPassportOut, now: Date = new Date()): string {
  const parts = [`Версия ${p.version}`, authorLine(p.updated_by, p.agent, p.via_agent)];
  if (p.updated_at) parts.push(clock(p.updated_at, now));
  if (p.source_md) parts.push(`источник: ${p.source_md}`);
  return parts.filter(Boolean).join(" · ");
}

/** Spec 062: a dossier or a summary holds this many characters at most. */
export const MEMORY_LIMIT = 3000;

/** Spec 062: how far the summary reaches, what came after it, and whether the manager owes it. */
export function summaryLine(s: Pick<ChatSummaryOut, "through_at" | "behind" | "due">, now: Date = new Date()): string {
  const parts = [s.through_at ? `доведена до сообщения ${clock(s.through_at, now)}` : ""];
  if (s.behind > 0) parts.push(`после неё сообщений: ${s.behind}`);
  if (s.due) parts.push("менеджер должен её обновить");
  return parts.filter(Boolean).join(" · ");
}

/** One saved text of the passport in a line. */
export function revisionLine(r: ClientPassportRevisionOut, now: Date = new Date()): string {
  const parts = [`v${r.version}`, authorLine(r.author, r.agent, r.via_agent), clock(r.created_at, now)];
  if (r.source_md) parts.push(`источник: ${r.source_md}`);
  return parts.filter(Boolean).join(" · ");
}
